import React, { useContext } from "react";
import "./home.css";
import "./connectingScreen.css";
import { DataLayerContext, LoadOptions } from "../dataLayer/DataLayer";
import { HomeBrand } from "./components/homeBrand";
import { IModalDialogConfig, ModalDialog } from "./components/modalDialog";
import { DictionaryContext } from "../dataLayer/Dictionary";
import { googleAuth } from "../auth/googleAuth";
import { appleAuth } from "../auth/appleAuth";
import { signOut as signOutAll } from "../auth/authState";
import { preloadLogo } from "./components/preloadLogo";
import { hideNativeSplash } from "./components/nativeSplash";
import { buildErrorReportMessage } from "../feedbackDraft";

/**
 * Minimum time the connecting screen stays up before advancing, so the logo
 * fly-in always completes and a fast reconnect never flickers. The logo settles
 * at ~980ms (220ms delay + 760ms travel), so this leaves a beat of the loading
 * indicator afterward. Measured from when the animation actually starts (i.e.
 * once the logo is decoded), not from mount.
 */
const MIN_VISIBLE_MS = 1400;

/** How long the "demo" mode (test scenario) holds before landing on Home. */
const DEMO_HOLD_MS = 2400;

/**
 * Safety cap on how long we hold the fly-in waiting for the logo to decode. The
 * preload promise resolves on load/error, so this is just belt-and-braces to
 * guarantee we never stall the launch on a slow/failed image decode.
 */
const LOGO_READY_TIMEOUT_MS = 2000;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export type ConnectingMode = "load" | "demo" | "static";

export interface ConnectingScreenProps {
  /**
   * - `"load"`   — real boot: run `dataLayer.load()`, then land on Home (held
   *                for at least MIN_VISIBLE_MS). Handles error / account-switch.
   * - `"demo"`   — test scenario: hold, then land on Home. No backend contact.
   * - `"static"` — presentational only (e.g. embedded in the sign-in screen
   *                during native silent sign-in). The parent decides when to
   *                move on; this component does nothing but animate.
   */
  mode: ConnectingMode;
  /** Line under the loading dots. */
  caption?: string;
}

/**
 * The launch "assembling" screen shown to an already-signed-in player while we
 * restore their session. It renders the real Home header (via the shared
 * {@link HomeBrand}) with the logo flying in from center to its header slot,
 * while the body shows a loading indicator. When the session is ready it hands
 * off to Home — because the header markup is identical, the swap is seamless:
 * the header stays put and only the body fills in.
 */
export function ConnectingScreen({
  mode,
  caption = "Getting the latest\u2026",
}: ConnectingScreenProps) {
  const dataLayer = useContext(DataLayerContext);
  const dictionary = useContext(DictionaryContext);

  const [modalDialogConfig, setModalDialogConfig] = React.useState<
    IModalDialogConfig | undefined
  >();

  // The fly-in only starts once the mascot is decoded, so it never animates a
  // blank or half-loaded image. Until then the animated pieces stay hidden.
  const [logoReady, setLogoReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    // Gate the fly-in on the logo being decoded. Record when the animation
    // actually starts so the minimum-visible hold covers the full fly-in
    // rather than being measured from a mount that may precede the paint.
    let animationStartedAt = 0;
    const readyPromise = Promise.race([preloadLogo(), delay(LOGO_READY_TIMEOUT_MS)]).then(() => {
      if (cancelled) return;
      animationStartedAt = Date.now();
      // Start the web entrance and dismiss the native splash together, so the
      // splash fades out exactly as the logo begins its fly-in — no blank gap
      // and no "splash logo vs. web logo" double-image. No-op in web browsers.
      setLogoReady(true);
      hideNativeSplash();
    });

    if (mode === "static") {
      return () => {
        cancelled = true;
      };
    }

    /** Hold the screen until the fly-in has had its minimum-visible window. */
    async function settle(): Promise<void> {
      await readyPromise;
      const remaining = MIN_VISIBLE_MS - (Date.now() - animationStartedAt);
      if (remaining > 0) {
        await delay(remaining);
      }
      if (!cancelled) {
        const operationsRouteRequested = dataLayer.consumeOperationsRouteRequest();
        dataLayer.resetScreenStack("Home", operationsRouteRequested ? "/" : undefined);
        // If the user arrived via a friend-invite deep link (/?invite=code),
        // drop them straight on the Friends screen where the "X invited you —
        // Accept?" banner lives, instead of making them dig Home → Leaderboard →
        // Manage friends. Home stays underneath so Back returns there. This also
        // covers the sign-in detour: after a required login the boot re-runs and
        // lands them right back on the invite.
        if (operationsRouteRequested) {
          dataLayer.openOperationsDashboard();
        } else if (dataLayer.pendingInvite.value) {
          dataLayer.navigateTo("Friends");
        }
      }
    }

    if (mode === "demo") {
      readyPromise
        .then(() => delay(DEMO_HOLD_MS))
        .then(() => {
          if (!cancelled) {
            dataLayer.resetScreenStack("Home");
          }
        })
        .catch((e) => console.error(e));
      return () => {
        cancelled = true;
      };
    }

    async function load(loadOptions?: LoadOptions) {
      const loadResult = await dataLayer.load(loadOptions);
      console.log("loadResult", loadResult);
      if (cancelled) return;

      switch (loadResult) {
        case "NotSignedIn":
          // Clear any stale cache; App.tsx will then render the SignInScreen.
          googleAuth.clearCachedToken();
          appleAuth.clearCachedToken();
          signOutAll();
          break;
        case "Error":
          setModalDialogConfig({
            title: "Error",
            message: (
              <div>
                <p>We're sorry, there was an error when trying to contact the server.</p>
                <p>Try again in a few moments, and please let us know about the problem!</p>
              </div>
            ),
            leftButtonText: "Try again",
            onLeftButtonClick: () => {
              window.location.reload();
            },
            rightButtonText: "Report bug",
            onRightButtonClick: () => {
              dataLayer.openFeedback(
                buildErrorReportMessage("loading my game and contacting the server"),
              );
            },
          });
          break;
        case "ConfirmUseNewAccountInsteadOfSavedOne":
          setModalDialogConfig({
            title: "Switch Accounts?",
            message: (
              <div>
                <p>
                  You're already logged in to one account on this device, but you opened Wirder
                  using a link to a different account.
                </p>
                <p>Do you want to use your saved account, or switch to the one from the link?</p>
              </div>
            ),
            leftButtonText: "Use Saved",
            onLeftButtonClick: () => {
              setModalDialogConfig(undefined);
              load("UseSavedAccount").catch((e) => console.error(e));
            },
            rightButtonText: "Use Link",
            onRightButtonClick: () => {
              setModalDialogConfig(undefined);
              load("OverrideSavedAccountWithNewOne").catch((e) => console.error(e));
            },
          });
          break;
        case "Success":
          await settle();
          break;
      }
    }

    // Prewarm dictionary
    dictionary.isValidWord("CAT").catch((e) => console.error(e));

    // Start the load of the session
    load().catch((e) => console.error(e));

    return () => {
      cancelled = true;
    };
  }, [dataLayer, dictionary, mode]);

  return (
    <div className={`screen home connecting${logoReady ? " is-ready" : ""}`}>
      <div className="home__content">
        <HomeBrand logoClassName="connecting__logo" />
        <div className="connecting__body">
          <div className="connecting__dots" aria-label="Loading">
            <i></i>
            <i></i>
            <i></i>
          </div>
          {caption && <div className="connecting__caption">{caption}</div>}
        </div>
      </div>
      <ModalDialog dialogConfig={modalDialogConfig}></ModalDialog>
    </div>
  );
}
